import React, { useState } from 'react';
import { X, FileText, Download, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgreementModal = ({ 
  isOpen, 
  onClose, 
  agreementType, // "NDA" or "RCA"
  projectId,
  projectTitle,
  userRole, // "owner" or "collaborator"
  agreementContent,
  onSigned
}) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSign = async () => {
    if (!agreed) {
      setError(t('iHaveReadAndAgree'));
      return;
    }

    setSigning(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: '',
          project_id: projectId,
          agreement_type: agreementType,
          signatory_id: user.id,
          signatory_name: user.name,
          signatory_email: user.email,
          signatory_role: userRole,
          language: language,
          signed_at: '',
          signature_hash: '',
          agreement_content: agreementContent,
          filled_fields: null
        }),
      });

      // Parse response body once
      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error(responseText || 'Failed to parse response');
      }

      // Check for errors after parsing
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to sign agreement');
      }
      
      // Download PDF automatically
      if (result.signature_id) {
        const pdfUrl = `${BACKEND_URL}/api/agreements/download/${result.signature_id}`;
        window.open(pdfUrl, '_blank');
      }
      
      // Call onSigned callback
      if (onSigned) {
        onSigned(result);
      }

      // Close modal
      onClose();
      
      // Show success message
      alert(`${agreementType} signed successfully!\nDigital Fingerprint: ${result.signature_hash?.substring(0, 16)}...`);

    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {agreementType === 'NDA' ? t('nonDisclosureAgreement') : t('researchCollaborationAgreement')}
              </h2>
              <p className="text-sm text-gray-500">{projectTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose max-w-none">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-700">
                {agreementContent}
              </pre>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  setError('');
                }}
                className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 font-medium">
                {t('iHaveReadAndAgree')}
              </span>
            </label>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSign}
            disabled={!agreed || signing}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
              agreed && !signing
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CheckCircle className="h-5 w-5" />
            {signing ? t('saving') : t('signAgreement')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;
